'use client';

import { useEffect } from 'react';
import { useAdminMode } from '@/hooks/useAdminMode';

/**
 * Next.js 개발 표시기(dev tools 배지)를 관리자 로그인 상태에서만 노출.
 *
 * 이 배지는 Next 가 React 트리 밖의 `<nextjs-portal>` 호스트(open shadow root) 안
 * `[data-next-badge-root]` 로 렌더링하므로 조건부 렌더링이 불가능하다. JS 로 shadow root
 * 에 접근해 비관리자에겐 숨기고, 관리자에겐 보인다. (개발 모드에서만 존재 — 프로덕션 빌드엔
 * 배지 자체가 없어 no-op)
 */
export default function DevIndicatorGate() {
  // 인증 확인 중(loading)에는 비관리자로 간주해 숨김 — 일반 방문자에게 깜빡임 방지.
  // 관리자로 확정되면 다시 노출.
  const { isAdmin } = useAdminMode();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;

    const apply = () => {
      document.querySelectorAll('nextjs-portal').forEach((host) => {
        const root = (host as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot;
        const badge = root?.querySelector<HTMLElement>('[data-next-badge-root]');
        if (badge) badge.style.display = isAdmin ? '' : 'none';
      });
    };

    apply();
    // 배지 포털은 비동기로 body 에 mount 되므로 관찰 후 재적용
    const observer = new MutationObserver(apply);
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [isAdmin]);

  return null;
}
